import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = useCallback(() => setActiveModal(null), []);
  return <>
      <motion.footer className="w-full bg-deep py-12 px-6 border-t border-white/5" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} data-id="element-99">
        <div className="max-w-2xl mx-auto text-center" data-id="element-100">
          <p className="font-body text-sm text-cream-faint leading-relaxed mb-3" data-id="element-101">
            The 3AM Reset is an educational resource and does not replace
            medical advice. If you suspect a sleep disorder, please consult a
            healthcare professional.
          </p>

          <p className="font-body text-sm text-cream-faint leading-relaxed mb-3" data-id="element-102">
            This site is not a part of the Facebook website or Facebook Inc.
            Additionally, this site is NOT endorsed by Facebook in any way.
            FACEBOOK is a trademark of FACEBOOK, Inc.
          </p>

          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-103">
            <button onClick={() => setActiveModal('privacy')} className="font-body text-sm text-cream-faint underline underline-offset-2 decoration-cream-faint/40 hover:text-teal-light hover:decoration-teal-light/40 transition-colors duration-200 cursor-pointer" data-id="element-104">
              Privacy Policy
            </button>
            <span className="text-cream-faint/40 text-sm" aria-hidden="true" data-id="element-105">
              ·
            </span>
            <button onClick={() => setActiveModal('terms')} className="font-body text-sm text-cream-faint underline underline-offset-2 decoration-cream-faint/40 hover:text-teal-light hover:decoration-teal-light/40 transition-colors duration-200 cursor-pointer" data-id="element-106">
              Terms
            </button>
          </div>

          <p className="font-body text-sm text-cream-faint" data-id="element-107">
            © {new Date().getFullYear()} The 3AM Reset. All rights reserved.
          </p>
        </div>
      </motion.footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-108">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} data-id="element-109">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70" onClick={closeModal} aria-hidden="true" data-id="element-110" />

            {/* Content Card */}
            <motion.div className="relative bg-deep-card rounded-xl shadow-2xl shadow-black/50 w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden border border-white/5" initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.98
        }} transition={{
          duration: 0.25,
          ease: [0.25, 0.1, 0.25, 1]
        }} role="dialog" aria-modal="true" aria-label={activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Use'} data-id="element-111">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/5 flex-shrink-0" data-id="element-112">
                <h2 className="font-heading text-xl font-semibold text-cream" data-id="element-113">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Use'}
                </h2>
                <button onClick={closeModal} className="text-cream-faint hover:text-cream transition-colors duration-150 p-1 -mr-1 rounded-md focus:outline-none focus:ring-2 focus:ring-teal/50" aria-label="Close" data-id="element-114">
                  <XIcon className="w-5 h-5" data-id="element-115" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="overflow-y-auto px-6 py-6 flex-1" data-id="element-116">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-117" /> : <TermsContent data-id="element-118" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <div className="space-y-5 font-body text-sm text-cream-muted leading-relaxed" data-id="element-119">
      <p className="text-cream-faint text-xs" data-id="element-120">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-121">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-122">
          Information We Collect
        </h3>
        <p data-id="element-123">
          When you purchase The 3AM Reset, we collect your name, email address,
          and payment information necessary to process your order. Payment
          processing is handled by secure third-party providers — we do not
          store your credit card details.
        </p>
      </section>

      <section data-id="element-124">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-125">
          How We Use Your Information
        </h3>
        <p data-id="element-126">
          We use your information to deliver the ebook, send your purchase
          confirmation, and provide any product updates. We may also send
          occasional emails related to The 3AM Reset. You can unsubscribe at any
          time.
        </p>
      </section>

      <section data-id="element-127">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-128">
          Cookies
        </h3>
        <p data-id="element-129">
          Our website may use cookies and similar technologies to improve your
          browsing experience and analyze site traffic. You can control cookie
          preferences through your browser settings.
        </p>
      </section>

      <section data-id="element-130">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-131">
          Third Parties
        </h3>
        <p data-id="element-132">
          We do not sell or rent your personal information. We may share data
          with trusted service providers (payment processing, email delivery)
          solely to operate our business. These providers are obligated to
          protect your information.
        </p>
      </section>

      <section data-id="element-133">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-134">
          Your Rights
        </h3>
        <p data-id="element-135">
          You may request access to, correction of, or deletion of your personal
          data at any time. To make a request, reach out through the channels
          provided at the time of purchase.
        </p>
      </section>

      <section data-id="element-136">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-137">
          Changes to This Policy
        </h3>
        <p data-id="element-138">
          We may update this Privacy Policy from time to time. Any changes will
          be reflected on this page with an updated revision date.
        </p>
      </section>
    </div>;
}
function TermsContent() {
  return <div className="space-y-5 font-body text-sm text-cream-muted leading-relaxed" data-id="element-139">
      <p className="text-cream-faint text-xs" data-id="element-140">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-141">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-142">
          License
        </h3>
        <p data-id="element-143">
          Upon purchase, you receive a personal, non-transferable license to
          access and use The 3AM Reset ebook for your own private,
          non-commercial use. You may not redistribute, resell, or share the
          content without written permission.
        </p>
      </section>

      <section data-id="element-144">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-145">
          Digital Delivery
        </h3>
        <p data-id="element-146">
          The 3AM Reset is a digital product delivered electronically. You will
          receive access to the ebook immediately or shortly after your purchase
          is confirmed.
        </p>
      </section>

      <section data-id="element-147">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-148">
          Refund Policy
        </h3>
        <p data-id="element-149">
          Due to the digital nature of this product, all sales are final. If you
          experience a technical issue accessing your purchase, we will work
          with you to resolve it promptly.
        </p>
      </section>

      <section data-id="element-150">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-151">
          Disclaimer
        </h3>
        <p data-id="element-152">
          The 3AM Reset is an educational resource and is not intended as
          medical advice, diagnosis, or treatment. The information provided is
          based on research and personal experience but should not replace
          consultation with a qualified healthcare professional. Results may
          vary.
        </p>
      </section>

      <section data-id="element-153">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-154">
          Limitation of Liability
        </h3>
        <p data-id="element-155">
          To the fullest extent permitted by law, The 3AM Reset and its creators
          shall not be liable for any indirect, incidental, or consequential
          damages arising from the use of this product.
        </p>
      </section>

      <section data-id="element-156">
        <h3 className="font-heading text-base font-semibold text-cream mb-2" data-id="element-157">
          Changes to These Terms
        </h3>
        <p data-id="element-158">
          We reserve the right to update these terms at any time. Continued use
          of the product after changes constitutes acceptance of the revised
          terms.
        </p>
      </section>
    </div>;
}